// The Reports rail: the questions the built-in views answer, in the groups the
// owner chose, and the pure parts of each question's one answer (its chart, the
// first rows of its table, the slots outside the branch's hours).
import type { DayHours, ReportRow } from '../lib/types'
import { monthName } from '../lib/i18n/format'
import { type StaffKey, type StaffLanguage } from '../lib/staffText'
import { OVERVIEW, VIEWS, isBreak, sameLayout, validLayout, type BreakDim, type Layout, type MRow, type Matrix, type MatrixSort, type View, type Visual } from './insightCalc'
import type { Mark } from './insightVisuals'

export type RailIcon = 'overview' | 'hours' | 'peak' | 'weekdays' | 'channels' | 'waits' | 'compare' | 'desks' | 'people' | 'weekly' | 'monthly' | 'tickets' | 'bookings' | 'saved' | 'add'

/** A built-in view on the rail, by its id in VIEWS. */
export type RailItem = { view: string; icon: RailIcon }
export type RailGroup = { key: StaffKey | null; items: RailItem[] }

export const RAIL: RailGroup[] = [
  { key: null, items: [{ view: 'overview', icon: 'overview' }] },
  {
    key: 'insight.rail.flow',
    items: [
      { view: 'hours', icon: 'hours' },
      { view: 'busy15', icon: 'peak' },
      { view: 'weekdays', icon: 'weekdays' },
      { view: 'channels', icon: 'channels' },
    ],
  },
  {
    key: 'insight.rail.waiting',
    items: [
      { view: 'waits', icon: 'waits' },
      { view: 'compare', icon: 'compare' },
    ],
  },
  {
    key: 'insight.rail.desks',
    items: [
      { view: 'desks', icon: 'desks' },
      { view: 'people', icon: 'people' },
    ],
  },
  {
    key: 'insight.rail.trend',
    items: [
      { view: 'weekly', icon: 'weekly' },
      { view: 'monthly', icon: 'monthly' },
    ],
  },
]

/** The built-in views on the rail, in its order: every one of VIEWS, once. */
export const railViews = () => RAIL.flatMap((g) => g.items.map((i) => VIEWS.find((v) => v.id === i.view)!))

// ---------------------------------------------------------------- Build a table

/** The rail item of the table built by hand, after the built-in questions. */
export const BUILDER = 'builder'
/** What Build a table opens with the first time: served per desk. */
export const STARTER: Layout = { rows: ['desk'], cols: [], values: [{ measure: 'served' }] }

/** The built-in question a layout and visual are, if any. */
export const builtInView = (layout: Layout, visual: Visual | null = null): View | undefined =>
  VIEWS.find((v) => sameLayout(v.layout, layout) && (v.visual ?? null) === visual)

/** The rail item a saved view opens on: the built-in whose layout it has, else
 *  Build a table; null for one that opens a visual of its own (the waiting bands). */
export const savedQuestion = (v: Pick<View, 'layout' | 'visual'>): string | null =>
  builtInView(v.layout, v.visual ?? null)?.id ?? (v.visual ? null : BUILDER)

/** A layout that is a table of its own: valid, no built-in question, and with a value to show. */
function ownTable(x: unknown): Layout | null {
  const l = validLayout(x)
  return l && l.values.length > 0 && !builtInView(l) ? l : null
}

/**
 * The layout Build a table opens with: the table on screen where it is one of
 * its own (made in Customise on another question); else the one last built
 * there; else the starter. A table with no values left opens as the starter.
 */
export function builderStart(onScreen: unknown, built: unknown): Layout {
  const b = validLayout(built)
  return ownTable(onScreen) ?? (b && b.values.length > 0 ? b : STARTER)
}

/** A table's sort as kept with it, or null. */
export function validSort(x: unknown): MatrixSort {
  const s = x as { col?: unknown; vi?: unknown; dir?: unknown } | null
  if (!s || typeof s !== 'object' || typeof s.col !== 'string' || !Number.isInteger(s.vi) || (s.vi as number) < 0 || (s.dir !== 1 && s.dir !== -1)) return null
  return { col: s.col, vi: s.vi as number, dir: s.dir }
}

/** What a question shows: the canvas of tiles, the waiting bands, one chart and
 *  its table, or Build a table's whole table. */
export type Answer = 'overview' | 'waits' | 'one' | 'table'
/** Decided by what is on screen, never by which view matched it: the overview's
 *  layout is the overview, whichever saved view shares it. */
export function answerOf(layout: Layout, visual: 'waits' | null): Answer {
  if (visual === 'waits') return 'waits'
  return sameLayout(layout, OVERVIEW) ? 'overview' : 'one'
}

// ---------------------------------------------------------------- the chart

const CLOCK_MIN: Partial<Record<BreakDim, number>> = { hour: 60, interval30: 30, interval15: 15 }
const COLUMN_DIMS = new Set<BreakDim>(['hour', 'interval15', 'interval30', 'weekday', 'day', 'week', 'month'])

/** The grouping a question's chart is drawn by: its outer row field, when that is a breakdown. */
export function chartDim(layout: Layout): BreakDim | null {
  const d = layout.rows[0]
  return d && isBreak(d) && layout.values.length > 0 ? d : null
}
export const columnsFor = (d: BreakDim) => COLUMN_DIMS.has(d)
export const isClock = (d: BreakDim) => CLOCK_MIN[d] !== undefined

/** "09", "09:00" or "09:30" as minutes from midnight; null for anything else. */
export function clockMinute(key: string): number | null {
  const m = /^(\d{1,2})(?::(\d{2}))?$/.exec(key)
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2] ?? 0)
  return h > 23 || min > 59 ? null : h * 60 + min
}

const clockLabel = (key: string) => {
  const m = clockMinute(key)
  return m === null ? key : `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

/**
 * Whether a time-of-day slot lies wholly outside the branch's hours on every day
 * it opens. The rows are summed over the period's days, so a slot open on any one
 * day counts as open: what is left is outside the hours whatever the day was.
 * No hours given is open all day (Branch.openingHours).
 */
export function outsideHours(hours: readonly DayHours[], dim: BreakDim, key: string): boolean {
  const len = CLOCK_MIN[dim]
  const start = clockMinute(key)
  if (!len || start === null || hours.length === 0) return false
  const end = start + len
  return !hours.some((h) => h.close > h.open && start < h.close && end > h.open)
}

/** The tickets that arrived in slots outside the hours, or 0 where that cannot be said. */
export function outsideCount(hours: readonly DayHours[], dim: BreakDim, rows: readonly ReportRow[] | null): number {
  if (!isClock(dim) || !rows) return 0
  return rows.reduce((n, r) => n + (outsideHours(hours, dim, r.key || r.label) ? r.counts.issued : 0), 0)
}

/**
 * The chart's marks from the unsorted matrix: each outer row's first value in
 * this period, the period before faintly where the table has it. Time-of-day
 * slots outside the hours are left off and the busiest of the rest is the peak.
 */
export function answerMarks(m: Matrix, layout: Layout, opts: { blank: string; hours?: readonly DayHours[] } = { blank: '—' }): Mark[] {
  const dim = chartDim(layout)
  if (!dim) return []
  const hasPeriod = layout.cols.includes('period')
  const now = hasPeriod ? { period: 'now' as const } : {}
  const marks: Mark[] = m.rows
    .filter((r) => r.level === 0 && r.pick.key !== undefined)
    .filter((r) => !opts.hours || !outsideHours(opts.hours, dim, r.pick.key!))
    .map((r) => ({
      key: r.pick.key!,
      // A time of day by where it starts: "09:30", not "09:30–09:45", so the ticks fit.
      label: isClock(dim) ? clockLabel(r.pick.key!) : r.label || opts.blank,
      value: m.value(r.pick, now, 0),
      ghost: hasPeriod ? m.value(r.pick, { period: 'before' }, 0) : undefined,
    }))
  if (isClock(dim)) {
    const top = Math.max(0, ...marks.map((k) => k.value ?? 0))
    if (top > 0) for (const k of marks) if (k.value === top) k.peak = true
  }
  return marks
}

// ---------------------------------------------------------------- the table's first rows

/** How a question's table opens: lists and times of day on their busiest, weekdays
 *  in their own order, days, weeks and months on the latest of them. */
export function previewMode(dim: BreakDim | null): 'rank' | 'natural' | 'latest' {
  if (!dim || dim === 'weekday') return 'natural'
  if (dim === 'day' || dim === 'week' || dim === 'month') return 'latest'
  return 'rank'
}

/**
 * The rows a question's table opens with: the first n, ranked by `rank` (highest
 * first) where the table has no order of its own, the slots outside the hours
 * after the rest. A table of groups keeps its order: its children follow their group.
 */
export function previewRows(flat: MRow[], n: number, opts: { rank?: (r: MRow) => number | null; outside?: (r: MRow) => boolean; latest?: boolean } = {}): MRow[] {
  if (flat.some((r) => r.level > 0)) return flat.slice(0, n)
  if (opts.latest) return flat.slice(-n)
  let list = [...flat]
  if (opts.rank) {
    const v = (r: MRow) => opts.rank!(r) ?? -Infinity
    list.sort((a, b) => v(b) - v(a))
  }
  if (opts.outside) list = [...list.filter((r) => !opts.outside!(r)), ...list.filter((r) => opts.outside!(r))]
  return list.slice(0, n)
}

// ---------------------------------------------------------------- the period, in the top bar

/** A period as the bar shows it: "8–14 Oct", "28 Sep – 14 Oct", the year where it is not this one. */
export function periodDates(from: string, to: string, lang: StaffLanguage, today: string): string {
  const ok = /^\d{4}-\d{2}-\d{2}$/
  if (!ok.test(from) || !ok.test(to)) return ''
  const year = from.slice(0, 4) !== today.slice(0, 4) || to.slice(0, 4) !== today.slice(0, 4)
  const day = (iso: string, withYear: boolean) => `${Number(iso.slice(8))} ${monthName(lang, Number(iso.slice(5, 7)) - 1, 'short')}${withYear ? ' ' + iso.slice(0, 4) : ''}`
  if (from === to) return day(to, year)
  if (from.slice(0, 7) === to.slice(0, 7)) return `${Number(from.slice(8))}–${day(to, year)}`
  return `${day(from, year && from.slice(0, 4) !== to.slice(0, 4))} – ${day(to, year)}`
}

/** The field list's search: every word typed is in the name, case aside. */
export function fieldMatches(name: string, query: string): boolean {
  const n = name.toLocaleLowerCase()
  return query
    .toLocaleLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => n.includes(w))
}
